import {useState, useMemo, useCallback} from 'react';

import {ShortcutData} from '../DrpCombinationManagementDialog';
import {CombinationInstance, CombinationValueInput} from '../useCombinations';

type DialogMode = 'list' | 'create' | 'edit' | 'edit-shortcuts';

export const useDialogState = () => {
  const [mode, setMode] = useState<DialogMode>('list');
  const [editingInstanceId, setEditingInstanceId] = useState<number | null>(null);
  const [label, setLabel] = useState('');
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [selectedCauses, setSelectedCauses] = useState<string[]>([]);
  const [selectedInterventions, setSelectedInterventions] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [editingShortcuts, setEditingShortcuts] = useState<ShortcutData[]>([]);

  const isValid = useMemo(() => {
    return label.trim().length > 0 && (selectedType != null || selectedCauses.length > 0 || selectedInterventions.length > 0);
  }, [label, selectedType, selectedCauses, selectedInterventions]);

  const isShortcutsValid = useMemo(() => {
    return editingShortcuts.filter((s) => s.combinationInstanceId > 0 && s.iconName && s.iconName.length > 0).length > 0;
  }, [editingShortcuts]);

  const resetForm = useCallback(() => {
    setLabel('');
    setSelectedType(null);
    setSelectedCauses([]);
    setSelectedInterventions([]);
    setError('');
    setEditingInstanceId(null);
  }, []);

  const loadInstanceForEdit = useCallback((instance: CombinationInstance) => {
    setLabel(instance.label || '');

    const typeValue = instance.values?.find((v) => v.key.dimension === 'DRP_TYPE');
    const causesValues = instance.values?.filter((v) => v.key.dimension === 'DRP_CAUSE') || [];
    const interventionsValues = instance.values?.filter((v) => v.key.dimension === 'DRP_INTERVENTION') || [];

    setSelectedType(typeValue?.text || null);
    setSelectedCauses(causesValues.map((v) => v.text || '').filter(Boolean));
    setSelectedInterventions(interventionsValues.map((v) => v.text || '').filter(Boolean));

    setEditingInstanceId(instance.id);
    setMode('edit');
  }, []);

  const initializeShortcuts = useCallback((shortcuts: ShortcutData[]) => {
    const initialShortcuts: ShortcutData[] = [];
    for (let i = 0; i < 4; i++) {
      const existing = shortcuts.find((s) => s.position === i + 1);
      initialShortcuts.push(
        existing || {
          combinationInstanceId: 0,
          iconName: '',
          position: i + 1,
        }
      );
    }
    setEditingShortcuts(initialShortcuts);
  }, []);

  const updateShortcut = useCallback((index: number, field: keyof ShortcutData, value: any) => {
    setEditingShortcuts((prev) => {
      const updated = [...prev];
      updated[index] = {...updated[index], [field]: value};
      return updated;
    });
  }, []);

  const getCombinationValues = useCallback((): CombinationValueInput[] => {
    return [
      ...selectedCauses.map((text) => ({
        dimension: 'DRP_CAUSE' as const,
        text,
      })),
      ...(selectedType ? [{dimension: 'DRP_TYPE' as const, text: selectedType}] : []),
      ...selectedInterventions.map((text) => ({
        dimension: 'DRP_INTERVENTION' as const,
        text,
      })),
    ];
  }, [selectedCauses, selectedType, selectedInterventions]);

  return {
    mode,
    setMode,
    editingInstanceId,
    label,
    setLabel,
    selectedType,
    setSelectedType,
    selectedCauses,
    setSelectedCauses,
    selectedInterventions,
    setSelectedInterventions,
    isSubmitting,
    setIsSubmitting,
    error,
    setError,
    editingShortcuts,
    setEditingShortcuts,
    isValid,
    isShortcutsValid,
    resetForm,
    loadInstanceForEdit,
    initializeShortcuts,
    updateShortcut,
    getCombinationValues,
  };
};
